// 音乐文件流式URL工具函数
// 用于隐藏实际文件路径，统一使用流式传输端点

// 播放策略：直链 webm-only，不再使用歌曲媒体流式接口（/api/music/:id/stream）。
// 说明：封面仍可走 asset=cover 的推导接口；歌词通常可直链同目录文件。
export const isSiteStreamEnabled = () => false;

/**
 * 将相对流/媒体 URL 转为当前页面 origin 的绝对 URL。
 * 用于手机/局域网访问时避免媒体请求被解析到错误的主机（如 localhost），从而出现“无法加载音频文件”。
 * @param {string} url - 相对路径（如 /api/music/1/stream?format=webm）或已是绝对 URL
 * @returns {string} 绝对 URL，或原样返回（非浏览器环境或无效时）
 */
export function getAbsoluteMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const u = url.trim();
  if (!u) return url;
  if (/^https?:\/\//i.test(u) || u.startsWith('//')) return u;
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      const path = u.startsWith('/') ? u : `/${u}`;
      return `${window.location.origin}${path}`;
    }
  } catch (_) {}
  return url;
}

function parseMetadata(music) {
  try {
    const raw = music?.metadata;
    return typeof raw === 'string' ? JSON.parse(raw) : (raw || null);
  } catch (_) {
    return null;
  }
}

function encodePathSegment(s) {
  try {
    return encodeURIComponent(String(s || '').trim());
  } catch (_) {
    return '';
  }
}

function buildDirectWebmPathFromTitle(music) {
  try {
    const album = String(music?.album || '').trim();
    const title = String(music?.title || '').trim();
    if (!album || !title) return null;
    const albumEnc = encodePathSegment(album);
    const titleEnc = encodePathSegment(title);
    if (!albumEnc || !titleEnc) return null;
    return `/musics/${albumEnc}/${titleEnc}/${titleEnc}.webm`;
  } catch (_) {
    return null;
  }
}

function normalizePublicPath(raw) {
  if (!raw) return null;
  let p = String(raw).trim();
  if (!p) return null;
  // strip query/hash
  p = p.split('#')[0].split('?')[0];

  // If file_path accidentally contains an absolute URL, keep only pathname.
  // This also avoids “phone requests its own localhost” when URL was hard-coded.
  try {
    const looksLikeUrl = /^https?:\/\//i.test(p) || p.startsWith('//');
    if (looksLikeUrl) {
      const base = (typeof window !== 'undefined' && window.location && window.location.origin)
        ? window.location.origin
        : 'http://localhost';
      const u = new URL(p, base);
      p = u.pathname || p;
    }
  } catch (_) {}

  p = p.replace(/\\/g, '/');

  // If it's a relative path like "musics/...", make it absolute from site root.
  // This prevents routes like "/cn/music" from resolving to "/cn/music/musics/...".
  if (!p.startsWith('/')) p = '/' + p;
  return p;
}

function getDirAndBaseFromFilePath(music) {
  const raw = normalizePublicPath(music?.file_path);
  if (!raw) return null;
  const normalized = String(raw);
  const parts = normalized.split('/');
  const file = parts.pop() || '';
  const base = file.replace(/\.[^/.]+$/, '');
  const dir = parts.join('/');
  if (!dir || !base) return null;
  return { dir, base };
}

function getDirectLyricsPath(music) {
  if (!music) return null;
  if (music.lyrics_file) return normalizePublicPath(music.lyrics_file);
  const type = String(music.lyrics_type || '').trim().toLowerCase().replace(/^\./, '');
  const info = getDirAndBaseFromFilePath(music);
  if (!type || !info) return null;
  return `${info.dir}/${info.base}.${type}`;
}

function getDirectCoverPath(music) {
  if (!music) return null;
  const info = getDirAndBaseFromFilePath(music);
  if (!info) return null;
  // 直链封面策略：只请求 .webp（不请求 .jpg/.png 等）
  return `${info.dir}/${info.base}.webp`;
}

export const getDerivedVideoPath = (music) => {
  const meta = parseMetadata(music) || {};
  const hasVideo = !!(meta?.has_video || meta?.video_ext || meta?.video_file);
  if (!hasVideo) return null;
  const info = getDirAndBaseFromFilePath(music);
  if (!info) return null;
  return `${info.dir}/VIDEO_${info.base}.webm`;
};

export const hasVideo = (music) => !!getDerivedVideoPath(music);

function getDerivedAudioWebmPath(music) {
  const info = getDirAndBaseFromFilePath(music);
  if (!info) return null;
  return `${info.dir}/${info.base}.webm`;
}

function pickDirectMediaPath(music, format = 'auto') {
  if (!music) return null;
  if (format === 'video') return getDerivedVideoPath(music) || null;
  return getDerivedAudioWebmPath(music);
}

/**
 * 生成音乐文件的流式传输URL（隐藏实际文件路径）
 * 音频统一请求 .webm，不再使用 auto。
 * @param {Object} music - 音乐对象
 * @param {string} format - 格式：'webm'（音频）、'video'
 * @returns {string|null} 流式URL，如果music或id不存在则返回null
 */
export const getMusicStreamUrl = (music, format = 'webm') => {
  if (!music) return null;

  // 直链：优先按 album+title 推导（webm-only），避免依赖 DB 旧的 file_path 扩展名
  if (format !== 'video') {
    const derived = buildDirectWebmPathFromTitle(music);
    if (derived) return derived;
  }

  // 回退：使用现有 file_path 推导（确保以 / 开头，避免被语言路由吞掉）
  return pickDirectMediaPath(music, format);
};

/**
 * 生成歌词文件的流式传输URL（隐藏实际文件路径）
 * @param {Object} music - 音乐对象
 * @returns {string|null} 流式URL，如果music或id不存在则返回null
 */
export const getLyricsStreamUrl = (music) => {
  // 直链：歌词与媒体同目录，按 lyrics_type 推导同名文件（/musics/...）
  return getDirectLyricsPath(music);
};

/**
 * 生成封面图片URL（封面与媒体同目录，后端按文件路径自动推导）
 * @param {Object} music - 音乐对象
 * @returns {string|null} 封面URL，如果music或id不存在则回退到直连路径
 */
export const getCoverStreamUrl = (music) => {
  if (!music) return null;
  // 直链：封面与媒体同目录同名，仅 .webp
  return getDirectCoverPath(music);
};

/**
 * 获取封面图片URL（优先使用流式URL，回退到原始路径）
 * @param {Object} music - 音乐对象
 * @returns {string|null} 封面图片URL
 */
export const getCoverImageUrl = (music) => {
  if (!music) return null;
  // 封面只请求 .webp：不回退 cover_image/cover_url（避免请求 .jpg）
  return getCoverStreamUrl(music) || null;
};

export const hasLyrics = (music) => !!(music && music.lyrics_type);
